// Punto de entrada: una sola llamada desde el main.tsx de cada portal.
//
//   mountVersionGuard({ portal: 'admin-portal', appName: 'el backoffice' });
//
// Se llama ANTES de montar React, a proposito: la recuperacion de chunks
// tiene que estar escuchando desde el primer modulo que se carga, y el cartel
// vive fuera del arbol de React para sobrevivir a una app que no monto.

import { installChunkErrorRecovery } from './chunk-recovery';
import { startVersionGuard, type GuardOptions, type VersionGuard } from './guard';
import { executeReload } from './reload';
import { readLocalStamp } from './stamp';
import { createUi } from './ui';

export interface MountOptions extends GuardOptions {
  /** Como se nombra el portal en el copy: "el backoffice", "el portal". */
  appName: string;
  /** Apagar el guard (ej: entorno de test). */
  enabled?: boolean;
}

let mounted: VersionGuard | null = null;

/** El guard montado, si lo hay. Para UI del portal (chip de version, etc). */
export function getVersionGuard(): VersionGuard | null {
  return mounted;
}

export function mountVersionGuard(options: MountOptions): VersionGuard | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (options.enabled === false) return null;
  // Idempotente: un doble montaje (StrictMode, HMR) no debe duplicar timers
  // ni carteles.
  if (mounted) return mounted;

  const local = readLocalStamp();
  installChunkErrorRecovery(() => local.buildId);

  const { appName, enabled: _enabled, ...guardOptions } = options;
  const guard = startVersionGuard(guardOptions);

  const ui = createUi(
    { appName },
    {
      onApply: () => guard.applyUpdate(),
      onDismiss: () => guard.dismiss(),
      onRetry: () => {
        // Los escalones 0 y 1 ya fallaron para llegar acá. Vamos directo al
        // mas fuerte: navegar con cache-buster en la URL.
        const target = guard.getState().remote?.buildId ?? local.buildId;
        void executeReload({ from: local.buildId, target, attempt: 2 });
      },
    },
  );

  const unsubscribe = guard.subscribe((state) => ui.render(state));

  mounted = {
    ...guard,
    stop() {
      unsubscribe();
      ui.destroy();
      guard.stop();
      mounted = null;
    },
  };

  // Expuesto para diagnostico desde la consola del browser:
  //   __authsysVersion.getState()   → que version corro y que veo publicada
  //   __authsysVersion.checkNow()   → forzar un chequeo
  (window as unknown as Record<string, unknown>)['__authsysVersion'] = mounted;

  return mounted;
}
